import { Component, OnInit } from '@angular/core';
import { ICON, ICON_STYLE } from '@core/util/icon';
import { MatterFileType, MatterStatus } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { AppState } from '@state/app/app.state';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { RecentlyViewedDispatch } from '@state/recently-viewed/recently-viewed.dispatch';
import { UserProfileState } from '@state/user-profile';
import { Observable } from 'rxjs';
import {
	PillDropdownResult,
	PillDropwdownOptions,
} from 'src/app/shared/atomic/atoms/pill-dropdown/pill-dropdown.component';
import { PillModes } from 'src/app/shared/atomic/atoms/pill/pill.component';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { TestHooks } from './top-nav-bar.hooks';
import { TopNavBarSelector } from './top-nav-bar.selector';

interface FinanceNumber {
	title: string;
	amount: number;
}

@UntilDestroy()
@Component({
	selector: 'app-top-sale-nav-bar',
	templateUrl: './top-nav-bar.component.html',
	styleUrls: ['./top-nav-bar.component.scss'],
	standalone: false,
})
export class TopNavBarComponent implements OnInit {
	testHooks = TestHooks;
	public ICON = ICON;
	public ICON_STYLE = ICON_STYLE;
	public pinned: boolean;
	public numbers: FinanceNumber[];
	public showActivityDropdown = false;
	public showNotesDropdown = false;
	public showDocumentsDropdown = false;
	public showChecklistDropdown = false;
	public matterStatus = MatterStatus;
	public showStewartPayloadWarning = false;
	public pillDropdownOptions: PillDropwdownOptions<MatterStatus>[] = [
		{
			name: 'Active',
			value: MatterStatus.ACTIVE,
			mode: PillModes.activeStatus,
		},
		{
			name: 'Closed',
			value: MatterStatus.CLOSED,
			mode: PillModes.closedStatus,
		},
		{
			name: 'Inactive',
			value: MatterStatus.INACTIVE,
			mode: PillModes.inactiveStatus,
		},
	];

	@Select(SelectedMatter.useHtmlDocuments)
	useHtmlDocuments$: Observable<boolean>;
	@ViewSelectSnapshot(SelectedMatter.id) matterId: string;
	@ViewSelectSnapshot(SelectedMatter.matterFileType)
	matterFileType: MatterFileType;
	@ViewSelectSnapshot(SelectedMatter.matterName) name: string;
	@ViewSelectSnapshot(SelectedMatter.matterNumber) number: string;
	@ViewSelectSnapshot(SelectedMatter.status) status: string;
	@Select(TopNavBarSelector.matterPinned) pinned$: Observable<boolean>;
	@SelectSnapshot(AppState.loadedFromLeap) loadedFromLeap: boolean;
	@SelectSnapshot(UserProfileState.getCurrentBusinessId) businessId;

	constructor(
		private navService: NavigationService,
		private matterDispatch: MatterDispatch,
		private recentlyViewedDispatch: RecentlyViewedDispatch,
		private analytics: AnalyticsService,
	) {}

	ngOnInit(): void {
		this.pinned$.pipe(untilDestroyed(this)).subscribe((pinned) => {
			this.pinned = pinned;
		});
	}

	onSaleNavNoteShow($event): void {
		this.showNotesDropdown = $event;
		if ($event) {
			this.showDocumentsDropdown = false;
			this.showChecklistDropdown = false;
		}
	}

	onSaleNavDocumentsShow($event): void {
		this.showDocumentsDropdown = $event;
		if ($event) {
			this.showNotesDropdown = false;
			this.showChecklistDropdown = false;
		}
	}

	onSaleChecklistShow($event): void {
		this.showChecklistDropdown = $event;
		if ($event) {
			this.analytics.logEvent(AnalyticsEvent.CHECKLIST_TOP_NAV_HOVER);
			this.showDocumentsDropdown = false;
			this.showNotesDropdown = false;
		}
	}

	goToMatter(): void {
		this.navService.goToMatter(this.matterId);
	}

	updateMatterStatus(
		$event: PillDropdownResult<PillDropwdownOptions<MatterStatus>>,
	): void {
		try {
			if ($event.result.value === MatterStatus.ACTIVE) {
				this.analytics.logEvent(
					AnalyticsEvent.TOP_NAV_BAR_FILE_STATUS_ACTIVE_CLICK,
					{ matterId: this.matterId },
				);
			}
			if ($event.result.value === MatterStatus.CLOSED) {
				this.analytics.logEvent(
					AnalyticsEvent.TOP_NAV_BAR_FILE_STATUS_CLOSED_CLICK,
					{ matterId: this.matterId },
				);
			}
			if ($event.result.value === MatterStatus.INACTIVE) {
				this.analytics.logEvent(
					AnalyticsEvent.TOP_NAV_BAR_FILE_STATUS_INACTIVE_CLICK,
					{ matterId: this.matterId },
				);
			}
			this.matterDispatch.SetStatus(this.matterId, $event.result.value);
		} catch (err) {
			console.log(err);
		}
	}

	pin() {
		this.analytics.logEvent(AnalyticsEvent.TOP_NAV_BAR_FILE_PIN_CLICK, {
			matterId: this.matterId,
		});
		this.recentlyViewedDispatch.Pin(this.matterId, !this.pinned);
	}
}
